import React, {
  ChangeEvent as ReactChangeEvent,
  FC,
  Fragment,
  useCallback,
  useState,
} from 'react';
import { Button, Form, Modal, Alert } from 'react-bootstrap';
import { useCreateEventMutation } from '../../hooks/data/use-create-event-mutation';
import { assocPath } from 'ramda';
import { preventedDefault } from '../../utils/events';
import { BadRequestError } from '../../api/errors/BadRequestError';
import { UnexpectedError } from '../../api/errors/UnexpectedError';
import styles from './create-event-modal.module.scss';

interface CreateEventModalProps {
  isVisible: boolean;
  onClose: () => void;
}

type FormValues = {
  title: string;
  destinations: { rtmpUrl: string; streamKey: string }[];
};

const initialFormValues = {
  title: '',
  destinations: [{ rtmpUrl: '', streamKey: '' }],
};

export const CreateEventModal: FC<CreateEventModalProps> = ({
  isVisible,
  onClose,
}) => {
  const [values, setValues] = useState<FormValues>(initialFormValues);
  const [formInvalid, setFormInvalid] = useState<boolean>(false);

  const {
    mutateAsync: createEvent,
    status: createEventStatus,
    error: createEventError,
    reset: resetCreateEvent,
  } = useCreateEventMutation();

  const resetErrors = useCallback(() => {
    if (formInvalid) {
      setFormInvalid(false);
    }
    if (!!createEventError) {
      resetCreateEvent();
    }
  }, [createEventError, formInvalid, resetCreateEvent]);

  const handleFormSubmit = preventedDefault(() => {
    const { title, destinations } = values;

    if (
      title &&
      destinations.length > 0 &&
      destinations.every(({ rtmpUrl, streamKey }) => rtmpUrl && streamKey)
    ) {
      createEvent({ title, destinations })
        .then(() => {
          onClose();
          setValues(initialFormValues);
        })
        .catch(() => void 0); // silent catch
    } else {
      setFormInvalid(true);
    }
  });

  const handleTitleChange = useCallback(
    (event: ReactChangeEvent<HTMLInputElement>) => {
      resetErrors();

      setValues((values) => ({
        ...values,
        title: event.target.value,
      }));
    },
    [resetErrors],
  );

  const handleDestinationChange = useCallback(
    (index, key) => (event: ReactChangeEvent<HTMLInputElement>) => {
      resetErrors();

      setValues((values) =>
        assocPath(['destinations', index, key], event.target.value, values),
      );
    },
    [resetErrors],
  );

  const addDestination = useCallback(() => {
    setValues((values) =>
      assocPath(
        ['destinations', values.destinations.length],
        {
          rtmpUrl: '',
          streamKey: '',
        },
        values,
      ),
    );
  }, []);

  return (
    <Modal show={isVisible} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Create new event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert
          dismissible
          show={formInvalid || !!createEventError}
          variant="danger"
          onClose={resetErrors}
          transition={false}
        >
          {(createEventError instanceof BadRequestError || formInvalid) &&
            'Make sure that all the fields below are filled in with correct values'}
          {createEventError instanceof UnexpectedError &&
            'Something went wrong. Please, retry later'}
        </Alert>
        <Form onSubmit={handleFormSubmit}>
          <Form.Group controlId="title">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter the title"
              value={values.title}
              onChange={handleTitleChange}
            />
          </Form.Group>

          <h5 className={styles.destinationsHeader}>Destinations:</h5>

          {values.destinations.map((destination, index) => (
            <Fragment key={index}>
              <Form.Group controlId={`destination-${index}-rtmp-url`}>
                <Form.Label>RTMP URL</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter the RTMP URL"
                  value={destination.rtmpUrl}
                  onChange={handleDestinationChange(index, 'rtmpUrl')}
                />
              </Form.Group>
              <Form.Group controlId={`destination-${index}-stream-key`}>
                <Form.Label>Stream key</Form.Label>
                <Form.Control
                  type="password"
                  value={destination.streamKey}
                  onChange={handleDestinationChange(index, 'streamKey')}
                />
              </Form.Group>
            </Fragment>
          ))}

          <div className={styles.destinationButtonContainer}>
            <Button variant="outline-primary" onClick={addDestination}>
              + Destination
            </Button>
          </div>

          <Button
            variant="primary"
            type="submit"
            disabled={createEventStatus === 'loading'}
          >
            Create event
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
